import axios from "axios";
import React, { Fragment, PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import i18n from "i18next";
import { toast } from "react-toastify";

import { Export } from "../../Common/Export/Export";
import {
  ListPrePlanningHeader,
  ListPrePlanningRow,
  ListTrackingHeader,
  ListTrackingRow,
  prePlanningSearchColumns,
  prePlanningSortColumns,
  searchColumns,
  sortColumns
} from "./List";
import { Panel, PanelHeader, PanelMobile } from "../../Layout/Panel";
import PanelTools from "../../Layout/Panel/PanelTools";
import { SortedList, SortedListPagination } from "../../Layout/SortedList";
import { FeedbackPopup } from "../../Layout/FeedbackPopup";
import { checkIsExternalUser } from "../../../helpers/Authorization/authorizationHelpers";
import { exportOrders } from "../../../helpers/xlsxHelpers";
import { getApiUrl } from "../../../helpers/apiUrlHelpers";
import { getCustomer, getCustomerPrePlanning, getCustomerPurchaseOrders, getUnitOfMeasurements } from "../../../actions";
import { getCustomerSelector, getIsLoadingSelector, getPrePlanningSelector, getPurchaseOrdersSelector } from "../../../selectors/selectors";
import { withNavigationHistory } from "../../Common/Navigation/withNavigationHistory";
import ListPagePanelSubNav from "./SubNav";
import ViewTypes from "../ViewTypes";

class ListPage extends PureComponent {
  static propTypes = {
    getCustomer: PropTypes.func.isRequired,
    getCustomerPrePlanning: PropTypes.func.isRequired,
    getCustomerPurchaseOrders: PropTypes.func.isRequired,
    breadcrumbStack: PropTypes.array,
    customer: PropTypes.object,
    customerId: PropTypes.string,
    isLoading: PropTypes.bool,
    isMobile: PropTypes.bool,
    prePlannings: PropTypes.array,
    purchaseOrders: PropTypes.array,
    userMetadata: PropTypes.object
  };

  static defaultProps = {
    breadcrumbStack: [],
    customer: {},
    customerId: "",
    isLoading: false,
    isMobile: false,
    prePlannings: [],
    purchaseOrders: [],
    userMetadata: {}
  };

  state = {
    sendReporting: false,
    viewState: {
      isTrackingSelected: true,
      tracking: {
        currentPage: 1,
        searchQuery: "",
        sort: {}
      },
      planning: {
        currentPage: 1,
        searchQuery: "",
        sort: {}
      }
    }
  };

  componentDidMount() {
    this.props.getCustomer(this.props.customerId);
  }

  handleSetViewState = item => this.setState({ viewState: { ...this.state.viewState, ...item } });

  handleSetSubnavViewState = item => {
    const { isTrackingSelected, planning, tracking } = this.state.viewState;
    const keyName = isTrackingSelected ? "tracking" : "planning";
    const value = isTrackingSelected ? tracking : planning;

    this.handleSetViewState({ [keyName]: { ...value, ...item } });
  };

  handleSearch = searchQuery => this.handleSetSubnavViewState({ searchQuery, currentPage: 1 });

  handleChangeUnits = units => {
    this.props.getCustomerPurchaseOrders(this.props.customerId, units);
    this.props.getCustomerPrePlanning(this.props.customerId, units);
  };

  handleChangeSort = sort => this.handleSetSubnavViewState({ sort });

  handleToggleSubnav = isTrackingSelected => this.handleSetViewState({ isTrackingSelected });

  handleChangePagination = currentPage => this.handleSetSubnavViewState({ currentPage });

  handleExportCSVClick = () => this.handleExportOrder(false);

  handleExportExcelClick = () => this.handleExportOrder(true);

  handleReportingButtonClick = async() => {
    this.setState({sendReporting: true});
    setTimeout(() => { this.setState({sendReporting: false});}, 1500);
    const lang = localStorage.getItem("language");
    const { unitOfMeasurements = {} } = this.props.userMetadata;
    const { WEIGHT = "MT", DIMENSION = "IN" } = unitOfMeasurements;
    const response = await axios.get(`${getApiUrl()}/customer/${this.props.customerId}/reporting`, {
      params: { weightUOM: WEIGHT, dimensionUOM: DIMENSION, lang }
    });
    toast(response.data, { type: toast.TYPE.INFO });
  };

  handleExportOrder = isXLSX => {
    const { customer, purchaseOrders, prePlannings, userMetadata } = this.props;
    exportOrders(customer, purchaseOrders, prePlannings, userMetadata.dateDisplayFormat, isXLSX);
  };  

  actions = [
    {
      key: "reporting",
      icon: "file",
      label: i18n.t("purchaseOrder.reporting"),
      onClick: this.handleReportingButtonClick
    },
    {
      label: i18n.t("export.csvFile"),
      icon: "table",
      key: "csv",
      onClick: this.handleExportCSVClick,
      showOnlyOnMobile: true
    },
    {
      label: i18n.t("export.excelFile"),
      icon: "table",
      key: "xlsx",
      onClick: this.handleExportExcelClick,
      showOnlyOnMobile: true
    }
  ];

  render() {
    const { customer, breadcrumbStack, isLoading, isMobile, prePlannings, purchaseOrders, userMetadata } = this.props;
    const { isTrackingSelected, tracking = {}, planning = {} } = this.state.viewState;
    const { currentPage, searchQuery, sort } = isTrackingSelected ? tracking : planning;
    const activeSubNav = isTrackingSelected ? ViewTypes.Tracking : ViewTypes.Planning;

    return (
      <Fragment>
        {isMobile ? (
          <PanelMobile className="purchase-order-list-page" isMobile={isMobile}>
            <PanelHeader
              title={i18n.t("purchaseOrder.orders")}
              id={customer.customerName || ""}
              actions={this.actions}
              isMobile={isMobile}
              isFirstPage={checkIsExternalUser()}
              customAction={<Export actions={this.actions.filter(x => x.showOnlyOnMobile)} />}
            />

            <ListPagePanelSubNav activeSubNav={activeSubNav} onToggleOrderView={this.handleToggleSubnav} />

            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />

            {isTrackingSelected && (
              <SortedList
                RowComponent={ListTrackingRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ListTrackingHeader}
                currentPage={currentPage}
                data={purchaseOrders}
                isLoading={isLoading}
                isMobile={isMobile}
                noResultMessage={i18n.t("table.noActiveOrdersMessage")}
                noResultTitle={i18n.t("table.noActiveOrdersTitle")}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                rowProps={{ customerId: this.props.customerId, breadcrumbStack }}
                searchQuery={searchQuery}
                searchValues={searchColumns}
                sortValues={sortColumns}
                sort={sort}
              />
            )}

            {!isTrackingSelected && (
              <SortedList
                RowComponent={ListPrePlanningRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ListPrePlanningHeader}
                currentPage={currentPage}
                data={prePlannings}
                isLoading={isLoading}
                isMobile={isMobile}
                noResultMessage={i18n.t("table.noUpcomingOrderMessage")}
                noResultTitle={i18n.t("table.noUpcomingOrderTitle")}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                rowProps={{ userMetadata, breadcrumbStack }}
                searchQuery={searchQuery}
                searchValues={prePlanningSearchColumns}
                sortValues={prePlanningSortColumns}
                sort={sort}
              />
            )}
          </PanelMobile>
        ) : (
          <Panel className="purchase-order-list-page" isMobile={this.props.isMobile}>
            <PanelHeader
              title={i18n.t("purchaseOrder.orders")}
              id={customer.customerName || ""}
              actions={this.actions}
              isMobile={this.props.isMobile}
              isFirstPage={checkIsExternalUser()}
              customAction={<Export actions={this.actions.filter(x => x.showOnlyOnMobile)} />}
            />

            <ListPagePanelSubNav activeSubNav={activeSubNav} onToggleOrderView={this.handleToggleSubnav} />

            <PanelTools
              searchQuery={searchQuery}
              isSearchVisible
              onSearch={this.handleSearch}
              isUnitsOfMeasureVisible
              changeUnits={this.handleChangeUnits}
            />

            {isTrackingSelected && (
              <SortedList
                RowComponent={ListTrackingRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ListTrackingHeader}
                currentPage={currentPage}
                data={purchaseOrders}
                isLoading={isLoading}
                noResultMessage={i18n.t("table.noActiveOrdersMessage")}
                noResultTitle={i18n.t("table.noActiveOrdersTitle")}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                rowProps={{ customerId: this.props.customerId, breadcrumbStack }}
                searchQuery={searchQuery}
                searchValues={searchColumns}
                sortValues={sortColumns}
                sort={sort}
              />
            )}

            {!isTrackingSelected && (
              <SortedList
                RowComponent={ListPrePlanningRow}
                PaginationComponent={SortedListPagination}
                HeaderComponent={ListPrePlanningHeader}
                currentPage={currentPage}
                data={prePlannings}
                isLoading={isLoading}
                noResultMessage={i18n.t("table.noUpcomingOrderMessage")}
                noResultTitle={i18n.t("table.noUpcomingOrderTitle")}
                onChangePagination={this.handleChangePagination}
                onChangeSort={this.handleChangeSort}
                rowProps={{ userMetadata, breadcrumbStack }}
                searchQuery={searchQuery}
                searchValues={prePlanningSearchColumns}
                sortValues={prePlanningSortColumns}
                sort={sort}
              />
            )}
            {this.state.sendReporting && <FeedbackPopup message={i18n.t("purchaseOrder.sendReporting")} />}
          </Panel>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const { customerId, breadcrumbStack } = state.app.routerParams;
  const actionList = [getCustomer.id, getCustomerPrePlanning.id, getCustomerPurchaseOrders.id, getUnitOfMeasurements.id];

  return {
    customerId,
    customer: getCustomerSelector(state, customerId),
    breadcrumbStack,
    isLoading: getIsLoadingSelector(state, actionList),
    isMobile: state.app.isMobile,
    prePlannings: getPrePlanningSelector(state),
    purchaseOrders: getPurchaseOrdersSelector(state),
    userMetadata: state.app.userMetadata
  };
};

export default connect(
  mapStateToProps,
  { getCustomer, getCustomerPrePlanning, getCustomerPurchaseOrders }
)(withNavigationHistory(ListPage));



// WEBPACK FOOTER //
// ./src/components/PurchaseOrder/ListPage/ListPage.js